import { Box, Typography } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import dayjs from "dayjs";

interface LeadsByDayChartProps {
  data: LeadsManagementData;
}

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// Builds every calendar day between the two (inclusive) yyyy-mm-dd bounds.
// Parsed as local midnight (not UTC) so the generated iso keys line up with
// toIsoDate's local getFullYear/getMonth/getDate below.
function buildDayRange(startIso: string, endIso: string): Date[] {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const days: Date[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 86400000) {
    days.push(new Date(t));
  }
  return days;
}

export default function LeadsByDayChart({ data }: LeadsByDayChartProps) {
  const { aggregations, goToFilteredLeads, start, end } = data;
  const brandColor = "#6200EE";

  const countByDate = new Map(aggregations.byDay.map((item) => [item.date, item.count]));

  // The chart must reflect the same start/end range as the rest of the
  // Analytics tab (and its own aggregations.byDay) — it previously always
  // plotted a fixed "last 14 days from now", which silently diverged from
  // whatever range was actually selected in the date filters above it.
  const days = buildDayRange(start, end);

  const series = days.map((day) => {
    const iso = toIsoDate(day);
    return {
      iso,
      label: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
      count: countByDate.get(iso) ?? 0,
    };
  });

  // Skip labels once the range grows past ~15 days so the x-axis doesn't
  // become an unreadable wall of overlapping text.
  const labelInterval = Math.max(0, Math.ceil(series.length / 15) - 1);

  return (
    <Box data-tracker="938" sx={{ mt: 2.5, p: "0"}}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, color: "#1a1a1a" }}>
          Leads by Day
        </Typography>
      </Box>
      <ResponsiveContainer width="100%" height={388}>
          {/* The click is handled on the whole chart rather than on each dot:
              on hover Recharts draws its active dot on top of ours, which
              swallowed the dot's own onClick. */}
          <LineChart
            data={series}
            margin={{ top: 8, right: 0, bottom: 8, left: 24 }}
            onClick={(state: any) => {
              const clickedDay = state?.activePayload?.[0]?.payload;
              if (clickedDay) goToFilteredLeads({ start: clickedDay.iso, end: clickedDay.iso });
            }}
            style={{ cursor: "pointer" }}
          >
            <CartesianGrid stroke="#eeeeee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9e9e9e" }} interval={labelInterval} />
            <YAxis
              orientation="right"
              allowDecimals={false}
              tick={{ fontSize: 12, fill: "#9e9e9e" }}
              axisLine={false}
              tickLine={false}
            />
            <RechartsTooltip
              content={({ active, payload }: any) =>
                active && payload?.length ? (
                  <Box
                    sx={{
                      bgcolor: "common.white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {dayjs(payload[0].payload.iso).format("D MMM")}
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: "#1a1a1a" }}>
                      {payload[0].value} lead{payload[0].value === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                ) : null
              }
            />
            <Line
              type="linear"
              dataKey="count"
              stroke={brandColor}
              strokeWidth={2.5}
              dot={(dotProps: any) => (
                <circle
                  key={dotProps.payload.iso}
                  cx={dotProps.cx}
                  cy={dotProps.cy}
                  r={4}
                  fill="#ffffff"
                  stroke={brandColor}
                  strokeWidth={2}
                />
              )}
            />
          </LineChart>
        </ResponsiveContainer>
    </Box>
  );
}
